/**
 * React auth gate for the WagePro admin console — session-cookie-based
 * (WagePro signup epic pivot off "Sign in with Ledger" OAuth). See
 * `apps/api/src/trpc/routers/wagepro_auth.ts`'s module docblock for the
 * full backend-side rationale.
 *
 * `resolveAuthBoot` is a pure function, deliberately factored out of the
 * component so it is unit-testable in `bun:test` without rendering React or
 * touching the DOM (wagepro-web has no jsdom setup today — see
 * `auth_context.test.ts`). `AuthProvider` is a thin wrapper: it supplies the
 * real `wageproAuth.me` call and holds the resulting state in `useState`.
 *
 * Cross-site cookie carve-out for the Ledger-SSO handoff (#25227). In
 * production `app.wagepro.ai` and `api.ledgerpro.ai` are different
 * registrable domains, so the Ledger session cookie (`SameSite=Lax`, see
 * `apps/api/src/lib/session_cookie.ts`) is never sent on the WagePro admin
 * console's `auth.me` fetch — that call resolves `{user:null}` even though
 * the user just consented on Ledger seconds ago. The grant-first branch's
 * bearer↔cookie cross-check therefore has THREE (not two) valid outcomes:
 * cookie identity matches, cookie identity mismatches (stale token —
 * clear + fall through), and NO cookie identity at all (the cross-site
 * case). The third case is honoured by booting from the bearer identity —
 * the cross-check exists only to stop A's stale token pairing with B's
 * COOKIE, so with no cookie there is nothing to mix. A THROW from
 * `fetchIdentity` (network, 5xx, TOO_MANY_REQUESTS) is NOT treated as
 * "no cookie": it falls through without clearing, so a transient error
 * on B's boot cannot silently promote A's stale token into B's org. Boot
 * traffic itself stays cookie-only (the admin tRPC client's bearer
 * fallback flag is off until we resolve case (c) — see `lib/trpc.ts`'s
 * `setAdminBearerFallback`), so a same-site / standalone user's boot
 * makes zero extra bearer requests. See
 * `docs/adr/20260922_02_origin-aware-samesite-session-cookie.md` for the
 * cookie-side context this closes the loop on.
 *
 * Boot is now a SINGLE round trip: `wageproAuth.me` resolves identity AND
 * the company/entity grant together in one query (the backend join in
 * `wagepro_auth.ts`'s `me` procedure), so there is no separate "fetch
 * identity, then fetch a scoped grant" two-step sequence to retry
 * independently — unlike the OAuth-era boot this replaces, a transient
 * failure here is just "unauthenticated, try again" (the user re-submits
 * `/login`), not a distinct `grant_error` state. `AuthStatus` KEEPS the
 * `error`/`grant_error` members (and `main.tsx`/`auth_gate_screen.ts`/
 * `session_boot_states.tsx` keep their branches for them) purely so those
 * three files — which sit outside this migration's scope and are shared
 * with concepts this module no longer produces — do not need to change;
 * `resolveAuthBoot` below simply never returns either value anymore. See
 * the plan file cited in the PR body for why this is a deliberate,
 * documented deviation from "remove the OAuth machinery" rather than an
 * oversight: `oauth_pkce.ts`/`token_store.ts`/`oauth_entity_context.ts`
 * remain load-bearing for the EMPLOYEE ESS invite-accept flow
 * (`invite_accept_screen.tsx`, `data/use_my_pay.ts`,
 * `lib/auth/step_up.ts`), which stays on OAuth/Personal-Workspace by
 * explicit design (ADR-030) — ripping those modules out would break that
 * flow for no benefit.
 *
 * `OAuthGrant`/`AuthIdentity`/`AuthContextValue`/`AuthStatus` KEEP their
 * exact field names and shapes so the ~20 downstream consumers of
 * `useAuth()`/`useOAuthEntityContext()` (every `data/use_*.ts` payroll hook,
 * `oauth_pay_run_post_actions.ts`, `oauth_statutory_filing.ts`, …) need ZERO
 * changes — `grant.firmId`/`grant.entityId` are now sourced from
 * `wageproAuth.me`'s `firm_id`/`entity_id` instead of `auth.oauthGrant`'s
 * OAuth-scoped claim, and `grant.mode`/`grant.scopes` are populated with
 * fixed "always full read+write" values (a session-cookie WagePro admin
 * always has full access — `payroll.ts`'s OAuth-scope gate is a documented
 * no-op for session-cookie callers) rather than derived from a token's
 * actual granted scope set. This is a deliberate scope-limiting decision,
 * not an oversight — see the PR body.
 */
import * as React from 'react';
import {
  getBearerTrpcClient,
  getTrpcClient,
  isAdminBearerFallbackEnabled,
  setAdminBearerFallback,
} from '../trpc.js';
import { clearStoredTokens, getStoredTokens } from './token_store.js';
import { clearNativeSessionToken } from './native_session_store.js';
import { signOut as oauthSignOut } from './oauth_pkce.js';
import {
  DEV_AUTH_BYPASS_OPT_IN_VALUE,
  ensureDevSession,
  resolveDevAuthCredentials,
} from './dev_auth_bypass.js';
import {
  claim_device_notifications,
  clear_device_notifications,
} from '../../data/device_notifications.js';
import {
  claim_my_leave_cache,
  clear_my_leave_cache,
} from '../../data/my_leave_cache.js';
import { SignedInUserIdContext } from './signed_in_user_context.js';

/**
 * `error` and `grant_error` are RETAINED on the type (see module docblock)
 * but never produced by {@link resolveAuthBoot} below — `main.tsx`'s
 * `AuthGate` and `auth_gate_screen.ts`'s `resolveAuthGateScreen` still have
 * branches for them, which are now simply unreachable dead code rather than
 * removed, to avoid touching those two files for this migration.
 */
export type AuthStatus =
  | 'loading'
  | 'authenticated'
  | 'unauthenticated'
  | 'error'
  | 'grant_error'
  | 'signed_out'
  /**
   * A cookie identity was successfully resolved by `auth.me` (step 2 of
   * {@link resolveAuthBoot}) BUT the caller holds neither a WagePro-audience
   * OAuth grant (step 0 or 3) nor an active ESS employer link (step 4) — i.e. a
   * Ledger-first admin whose consent to WagePro has not (or no longer) been
   * granted, or was granted for a DIFFERENT session and the token was
   * cleared. Sending them to the plain WagePro `/login` credentials form
   * would 403 (`wageproAuth.login` rejects users with no `wagepro_companies`
   * row and no employee link — that is precisely the class of user that
   * needs the Ledger SSO handoff instead). {@link resolveAutoSignInPrompt}
   * therefore never fires for this status; the AuthGate redirect effect
   * navigates straight to `buildLedgerSsoLoginPath()` — the login screen's
   * launcher-style entry that starts the "Sign in with Ledger" PKCE
   * handoff on mount instead of showing the credentials form.
   */
  | 'needs_ledger_sso';

/** The minimal identity claim `wageproAuth.me` returns. */
export interface AuthIdentity {
  userId: string;
  email: string;
  /**
   * The name entered at signup (`wagepro_companies`/`users.full_name`) —
   * threaded through so `wagepro_store_identity.ts`'s `deriveSessionFromIdentity`
   * can show the account's REAL name (profile menu, dashboard greeting)
   * instead of guessing one from the email local-part.
   */
  fullName: string;
}

/**
 * The company/entity "grant" `wageproAuth.me` resolves — kept as the exact
 * pre-existing `OAuthGrant` shape (see module docblock) even though it is no
 * longer an OAuth grant at all: `firmId`/`entityId` are the WagePro
 * company's underlying `firm_id_ref`/`entity_id_ref`
 * (`wagepro_companies` — `apps/api/src/db/schema/control_plane/
 * wagepro_companies.ts`), and `mode`/`scopes` are fixed "full access"
 * values a session-cookie WagePro admin always has (see module docblock).
 */
export interface OAuthGrant {
  firmId: string | null;
  entityId: string | null;
  mode: 'read' | 'write' | null;
  scopes: string[];
}

/**
 * Fixed scope list `use_pay_runs.ts`'s `isWriteCapableGrant` checks for
 * (`PAYROLL_EMPLOYER_WRITE_SCOPE`). A session-cookie WagePro admin always
 * has full read+write on their own company's payroll — there is no
 * narrower-scoped session in this product today — so this is always the
 * value `resolveAuthBoot` below produces for an authenticated grant.
 */
const FULL_ACCESS_SCOPES: readonly string[] = [
  'payroll:employer:read',
  'payroll:employer:write',
];

/**
 * The scope set a SELF-SERVICE (employee) session holds. Deliberately excludes
 * both `payroll:employer:*` scopes, so `isWriteCapableGrant`
 * (`data/use_pay_runs.ts`) reports false for an employee and no write path in
 * the app is reachable from this session.
 */
const SELF_ONLY_SCOPES: readonly string[] = ['identity', 'payroll:self:read'];

/**
 * Which of the two session shapes this boot resolved.
 *
 * - `employer` — a WagePro company owner, resolved by `wageproAuth.me` from
 *   `wagepro_companies.owner_user_id`. Holds a full-access grant bound to the
 *   company's firm + entity.
 * - `employee` — someone who accepted an ESS invite
 *   (`essInviteSignup.acceptWithCredentials`). Has NO firm membership and no
 *   `wagepro_companies` row, so `wageproAuth.me` rejects them; their employer
 *   linkage lives in their own PersonalDO (`employer_links`).
 *
 * Load-bearing beyond bookkeeping: `resolveAuthGateScreen`
 * (`auth_gate_screen.ts`) uses it to skip the employer-only session-facts
 * gate, which an employee can never satisfy.
 */
export type SessionKind = 'employer' | 'employee';

/**
 * The employer an employee session is linked to, resolved from the caller's
 * OWN PersonalDO via `personalPayroll.listEmployerLinks` — never from a
 * control-plane membership table (an employee has no row in one).
 *
 * `entityName` is the only display fact an employee is entitled to read about
 * their employer; it is what `main.tsx` feeds the store as `company_name`.
 */
export interface EmployeeSelfContext {
  entityId: string;
  employeeId: string;
  entityName: string;
}

/**
 * Which of the three boot paths resolved this session — surfaced on the
 * context so tests/telemetry can distinguish "WagePro owns a company row for
 * this user" from "user consented through Ledger and holds an OAuth grant"
 * from "self-service employee accepted an ESS invite". `null` before the
 * first boot resolves, or when it resolves as unauthenticated.
 *
 * - `wagepro_company` — {@link resolveAuthBoot} step 1: `wageproAuth.me`
 *   returned a row (a legacy WagePro-first standalone signup owner). Grant is
 *   sourced from `wagepro_companies.firm_id_ref/entity_id_ref`.
 * - `ledger_grant`    — step 0 (grant-first, when a bearer token pair is
 *   stored and the token's identity matches the cookie identity) or step 3
 *   (no token stored): cookie session exists AND `auth.oauthGrant` reported
 *   a `(firmId, entityId)` pair. The Ledger-first case — a firm owner/admin
 *   consented through Ledger, with or without a `wagepro_companies` row.
 * - `employee_link`   — step 4: `personalPayroll.listEmployerLinks` resolved
 *   an active ESS invite acceptance.
 */
export type AuthBootSource =
  | 'wagepro_company'
  | 'ledger_grant'
  | 'employee_link';

export interface AuthContextValue {
  status: AuthStatus;
  identity: AuthIdentity | null;
  grant: OAuthGrant | null;
  /** Which session shape booted — `null` until `authenticated`. */
  sessionKind: SessionKind | null;
  /** Set only for an `employee` session; `null` for an employer. */
  employee: EmployeeSelfContext | null;
  /** Which of the three boot paths resolved this session — see {@link AuthBootSource}. */
  bootSource: AuthBootSource | null;
  signOut: () => Promise<void>;
  /** Re-runs the boot sequence (`resolveAuthBoot`) from scratch. */
  retryBoot: () => void;
}

const AuthContext = React.createContext<AuthContextValue | undefined>(
  undefined,
);

/** The shape `wageproAuth.me` resolves — see `apps/api/src/trpc/routers/wagepro_auth.ts`. */
export interface WageproMeResult {
  user_id: string;
  email: string;
  full_name: string;
  company_id: string;
  company_name: string;
  entity_id: string;
  firm_id: string;
  role: string;
}

/** The subset of `auth.me`'s response the boot sequence reads (steps 0 and 2). */
export interface AuthIdentityResult {
  user: { id: string; email: string; full_name: string } | null;
}

/** The subset of `personalPayroll.listEmployerLinks`' response the fallback reads. */
export interface EmployerLinksResult {
  links: Array<{
    entity_id: string;
    employee_id: string;
    entity_name: string;
  }>;
}

/**
 * Minimal projection of `auth.oauthGrant` — the four fields
 * {@link resolveAuthBoot} promotes into an authenticated `ledger_member`
 * session. Deliberately narrow so tests can construct one without a fake
 * tRPC response. `firmId`/`entityId` mirror the `capability.firmId` /
 * `capability.entityIds[0]` shape (see `auth.ts` L1114 — payroll capabilities
 * are single-entity, ADR-123, so `[0]` is unambiguous).
 */
export interface OAuthGrantClaim {
  firmId: string | null;
  entityId: string | null;
  mode: 'read' | 'write' | null;
  scopes: string[];
}

/**
 * Minimal shape {@link AuthBootDeps.fetchBearerIdentity} returns — the
 * `userId` claim {@link resolveAuthBoot}'s grant-first branch needs for its
 * bearer↔cookie identity cross-check, PLUS the `email` needed for the
 * cross-site cookie carve-out below where the cookie identity is
 * unreachable and the bearer identity is the only identity source.
 * Deliberately narrower than {@link AuthIdentityResult}: `auth.identity` is
 * the PII-minimised endpoint mapped to the `identity` OAuth scope (see
 * `auth.ts`'s `identity` procedure) — it returns only `{ userId, email }`,
 * both of which this shape mirrors.
 */
export interface BearerIdentityResult {
  userId: string;
  email: string;
}

export interface AuthBootDeps {
  /** `wageproAuth.me` query — resolves identity + company/entity grant in ONE round trip. */
  fetchMe: () => Promise<WageproMeResult>;
  /**
   * `auth.me` — the generic, cookie-reachable identity query. Two callers:
   * the grant-first pre-step (step 0) reads it as the COOKIE side of the
   * bearer↔cookie cross-check, and step 2 reads it once `wageproAuth.me`
   * has rejected, to prove a session exists before the grant/ESS branches.
   *
   * In the Ledger-SSO handoff (#25227), this call resolves `{user:null}`
   * in production because it travels over the cookie transport and the
   * Ledger `SameSite=Lax` cookie is withheld cross-site (`app.wagepro.ai`
   * → `api.ledgerpro.ai`). The grant-first branch treats that outcome
   * not as "fail" but as "no cookie to cross-check against". A REJECTED
   * promise (network, 5xx, TOO_MANY_REQUESTS) is deliberately NOT
   * conflated with `{user:null}` — see {@link resolveAuthBoot}'s
   * docblock for the boot decision.
   */
  fetchIdentity: () => Promise<AuthIdentityResult>;
  /**
   * `personalPayroll.listEmployerLinks` — reads the caller's OWN PersonalDO.
   * Its `requireNonNullAudience`/`requireOAuthScope` guards no-op for a
   * first-party session-cookie caller, and it is one of the three procedures
   * inside the `requireAal2ExceptWageproSelfService` allowlist (#23208), so a
   * freshly-accepted employee with no TOTP can reach it.
   */
  fetchEmployerLinks: () => Promise<EmployerLinksResult>;
  /**
   * `auth.oauthGrant` — echoes the caller's OAuth capability, or `null` if no
   * OAuth token is held (a pure cookie session gets `null` — the endpoint
   * itself returns `{ firmId: null, entityId: null, mode: null, scopes: [] }`
   * for such callers, but the WagePro real implementation short-circuits and
   * returns `null` without a round trip when there is no bearer token).
   *
   * This is what serves #23952's Ledger-first case: after clicking "Allow
   * Access" on Ledger's consent screen, WagePro exchanges the code and holds
   * a WagePro-audience OAuth token bound to `(firmId, entityId)` — but the
   * user has no `wagepro_companies` row yet, so {@link fetchMe} rejects. The
   * grant is the ONLY way to know which entity the consent covered.
   */
  fetchOAuthGrant: () => Promise<OAuthGrantClaim | null>;
  /**
   * `auth.identity` OVER THE BEARER CLIENT — the minimal `{ userId, email }`
   * the WagePro-audience OAuth token authenticates as. Load-bearing for
   * {@link resolveAuthBoot}'s grant-first branch in two distinct ways:
   *
   *  1. Cross-check against the cookie identity when both are reachable.
   *     Without it, a stale token belonging to user A could be paired with
   *     user B's cookie identity, booting B into A's org (server-side data
   *     calls are cookie-authorised so it is NOT an IDOR — B lands in A's
   *     org with B's name, a wrong-org boot).
   *  2. The ONLY identity source in the Ledger-SSO cross-site case
   *     (#25227), where the cookie transport cannot reach the Ledger
   *     `SameSite=Lax` cookie at all (`app.wagepro.ai` →
   *     `api.ledgerpro.ai`). Cross-checking is moot when there is no
   *     cookie to mix with; the token was minted seconds ago by this
   *     user's own consent.
   *
   * The `identity` scope is included in every WagePro-audience token
   * (`AUDIENCE_SCOPE_FAMILIES.wagepro` in
   * `apps/api/src/trpc/oauth_allowlist.ts`), so this is always reachable
   * when a token is stored.
   */
  fetchBearerIdentity: () => Promise<BearerIdentityResult | null>;
  /**
   * True iff a WagePro-audience OAuth bearer token pair is stored locally
   * (`token_store.getStoredTokens() !== null`). Load-bearing for the
   * grant-first branch below: {@link resolveAuthBoot} only consults
   * {@link fetchOAuthGrant} ahead of {@link fetchMe} when this returns true,
   * so a cookie-only WagePro-first standalone owner (no token ever minted)
   * keeps the pre-#24241 fetchMe-first path and boots into their own company.
   */
  hasStoredToken: () => Promise<boolean>;
  /**
   * `clearStoredTokens()` — drops the WagePro-audience OAuth bearer token
   * pair. Called by {@link resolveAuthBoot}'s grant-first branch ONLY when
   * the token's bearer identity does not match the cookie session's identity
   * (a definitively stale token belonging to a different user). Never called
   * on transient failures (identity fetch throws) — those fall through
   * without clearing so a real token isn't wiped by a network hiccup.
   */
  clearBearerTokens: () => Promise<void>;
  /**
   * Toggles `lib/trpc.ts`'s `adminBearerFallbackEnabled` flag. Production
   * passes {@link setAdminBearerFallback}; tests pass a spy. Called
   * with `true` iff {@link resolveAuthBoot} resolves via case (c) of the
   * grant-first branch (Ledger-SSO cross-site handoff, #25227 — no
   * cookie identity, bearer identity is authoritative), and with `false`
   * on every other boot outcome so a re-boot after retry / a stale
   * previous case-(c) session leaves the admin tRPC client in its
   * pure-cookie default. Kept out of the pure-function return value on
   * purpose: `wageproAuth.login` / `.signup` also go through the same
   * admin client and must not attach a stale bearer, so the toggle needs
   * to be observable outside `resolveAuthBoot`'s pure return.
   */
  setBearerFallback: (enabled: boolean) => void;
}

export interface AuthBootResult {
  /**
   * The three possible boot outcomes:
   * - `authenticated` — some session shape resolved (`wagepro_company`,
   *   `ledger_grant`, or `employee_link`).
   * - `unauthenticated` — no session cookie at all (`auth.me` returned a
   *   null user, or its fetch itself failed). `AuthGate`'s redirect effect
   *   sends this to the WagePro `/login` credentials form.
   * - `needs_ledger_sso` — a cookie identity was resolved but neither an
   *   OAuth grant nor an employee link matched. The `/login` credentials
   *   form would 403 for these users (see {@link AuthStatus}), so
   *   `AuthGate`'s redirect effect sends them to
   *   `buildLedgerSsoLoginPath()` — the SSO handoff entry point — instead.
   */
  status: 'authenticated' | 'unauthenticated' | 'needs_ledger_sso';
  identity: AuthIdentity | null;
  grant: OAuthGrant | null;
  sessionKind: SessionKind | null;
  employee: EmployeeSelfContext | null;
  /** {@link AuthBootSource} that produced this result — `null` when unauthenticated / needs_ledger_sso. */
  bootSource: AuthBootSource | null;
}

const UNAUTHENTICATED_BOOT: AuthBootResult = {
  status: 'unauthenticated',
  identity: null,
  grant: null,
  sessionKind: null,
  employee: null,
  bootSource: null,
};

const NEEDS_LEDGER_SSO_BOOT: AuthBootResult = {
  status: 'needs_ledger_sso',
  identity: null,
  grant: null,
  sessionKind: null,
  employee: null,
  bootSource: null,
};

/**
 * Injectable dependencies for {@link performSignOut} — the sign-out side
 * effects, run in order, so the whole flow is exercised in `bun:test`
 * without a DOM or a real tRPC client.
 */
export interface SignOutDeps {
  /**
   * `auth.logout` — expires the session cookie server-side. `AuthProvider`
   * passes a no-op resolved promise for a bearer-backed session (case (c)
   * boot, #25227): that session has no server-side cookie state
   * `auth.logout` could reach when the request goes out bearer-only, so
   * skipping the call avoids the guaranteed FORBIDDEN + console.error on
   * every SSO sign-out. Server-side revocation for that session shape
   * happens via {@link clearBearerTokens} → `oauthSignOut()` →
   * `/oauth/revoke` instead.
   */
  logout: () => Promise<unknown>;
  /** Local native-shell token removal — `clearNativeSessionToken` in production. */
  clearNativeToken: () => Promise<void>;
  /**
   * `oauthSignOut()` in production — hits `/oauth/revoke` to invalidate
   * the WagePro-audience OAuth token pair server-side, then drops it from
   * browser storage. (Earlier revisions of this file wired
   * `clearStoredTokens()` here; that never revoked the token server-side,
   * so a signed-out user's Ledger consent grant stayed live until its
   * TTL. See #25227.) Load-bearing: without the local clear, a later
   * cookie-only login by a DIFFERENT user in the same browser would find
   * the leftover bearer token and enter step 0 of {@link resolveAuthBoot}.
   * The bearer↔cookie cross-check there is the last line of defence
   * against booting B into A's org; sign-out must not leave that stale
   * token behind to depend on it.
   */
  clearBearerTokens: () => Promise<void>;
  /**
   * Toggles `lib/trpc.ts`'s `adminBearerFallbackEnabled` flag off after
   * sign-out. Production passes {@link setAdminBearerFallback}; tests
   * pass a spy. Must run whether or not the server-side call succeeded —
   * a stuck-on flag on a signed-out user would keep attaching the (now-
   * cleared) bearer to every subsequent request until the next full page
   * reload.
   */
  setBearerFallback: (enabled: boolean) => void;
  /**
   * `clear_device_notifications()` — drops the bell's on-device "payslip
   * downloaded" entries. Each opens this user's salary PDF in one tap, so the
   * next person to sign in on a shared phone must not inherit them.
   */
  clearDeviceNotifications: () => void;
  /**
   * `clear_my_leave_cache()` drops My leave's in-memory balances and request
   * history (personal data kept for instant tab switches), so the next
   * person to sign in on a shared phone never sees them.
   */
  clearMyLeaveCache: () => void;
}

/**
 * The server + local half of sign-out, extracted from `AuthProvider` so the
 * ORDERING is unit-testable without rendering React.
 *
 * Two properties this function exists to guarantee:
 *
 *  1. `logout` still runs, and runs FIRST. On native the session now travels
 *     as a Bearer header, and `apps/api/src/trpc/context.ts` resolves that
 *     raw session id through the same `resolveSession` path a cookie takes —
 *     so `auth.logout` genuinely revokes it server-side. Dropping the call
 *     and only clearing local storage would leave a live session in KV until
 *     its TTL expired, which is a real security regression, not a tidy-up.
 *     It must run first, while the token is still readable and can still be
 *     presented as the credential that authorises its own revocation.
 *  2. BOTH local tokens are cleared even when `logout` REJECTS — the native
 *     session token AND the WagePro-audience OAuth bearer token pair. A
 *     network failure, an already-expired session, an offline device — none
 *     of those may leave a signed-out user still holding either credential
 *     for the next launch to replay. Hence the `finally`. Clearing the
 *     bearer pair specifically also closes the cross-user boot bug described
 *     on {@link SignOutDeps.clearBearerTokens}.
 *
 * Never throws: all three steps are best-effort and logged. The caller clears
 * its React state unconditionally afterwards, so a failure here degrades to
 * "the user is signed out locally, and a stale server session expires on its
 * own TTL" rather than a sign-out button that appears to do nothing.
 *
 * Consolidated from two independently-added sign-out helpers during the
 * epic/apps -> main rebase on 2026-09-10: main had added native-token
 * clearing (this function's original name/shape) while this branch's
 * #23958 commit had (pre-existing, from before that commit) bearer-token
 * clearing under the name `performSignOutSideEffects`. Both concerns are
 * real and independent (native app session vs. the Ledger OAuth consent
 * grant token pair), so this merges them into one call rather than running
 * `auth.logout` twice from two separate helpers.
 */
export async function performSignOut(deps: SignOutDeps): Promise<void> {
  try {
    await deps.logout();
  } catch (err) {
    console.error('[wagepro-web] sign-out request failed, continuing:', err);
  } finally {
    try {
      await deps.clearNativeToken();
    } catch (err) {
      console.error(
        '[wagepro-web] clearing the native session token failed:',
        err,
      );
    }
    try {
      await deps.clearBearerTokens();
    } catch (err) {
      console.error('[wagepro-web] clearing stored bearer tokens failed:', err);
    }
    try {
      deps.setBearerFallback(false);
    } catch (err) {
      console.error(
        '[wagepro-web] disabling the admin bearer fallback flag failed:',
        err,
      );
    }
    try {
      deps.clearDeviceNotifications();
    } catch (err) {
      console.error(
        '[wagepro-web] clearing on-device notifications failed:',
        err,
      );
    }
    try {
      deps.clearMyLeaveCache();
    } catch (err) {
      console.error('[wagepro-web] clearing the My leave cache failed:', err);
    }
  }
}

/**
 * Injectable dependencies for {@link performCookieLoginPostAction} — the
 * local side effects to run right after a successful cookie login via
 * `wageproAuth.login`.
 */
export interface CookieLoginPostActionDeps {
  /**
   * `clearStoredTokens()` in production — drops any WagePro-audience OAuth
   * bearer token pair left over from an earlier launch. Load-bearing for
   * #24241: without it, a stale token would win over the deliberate cookie
   * login in {@link resolveAuthBoot}'s grant-first branch, booting the user
   * into whatever organisation the leftover token grants instead of the one
   * they just typed credentials for.
   */
  clearBearerTokens: () => Promise<void>;
}

/**
 * Runs the local side effects for a successful cookie login. Currently just
 * clears any stored WagePro-audience OAuth bearer token pair — see the
 * {@link CookieLoginPostActionDeps.clearBearerTokens} docblock for why. Never
 * throws: a broken storage clear must not abort the login navigation.
 */
export async function performCookieLoginPostAction(
  deps: CookieLoginPostActionDeps,
): Promise<void> {
  try {
    await deps.clearBearerTokens();
  } catch (err) {
    console.error(
      '[wagepro-web] clearing stored bearer tokens after login failed:',
      err,
    );
  }
}

/**
 * Pure boot-sequence decision logic.
 *
 * Ordering rule (#24241): when a WagePro-audience OAuth bearer token pair is
 * stored, the token's `(firmId, entityId)` is the SOURCE OF TRUTH for which
 * organisation this boot resolves — `wageproAuth.me`'s LIMIT-1 select over
 * `wagepro_companies` cannot pick the right row when a firm admin owns one
 * row per connected Ledger entity (#23951), so consulting fetchMe first
 * would silently reroute the session to an arbitrary company. We therefore
 * try `auth.oauthGrant` AHEAD of `wageproAuth.me` when — and only when — a
 * token is stored. Cookie-only WagePro-first standalone owners (no token
 * ever minted) keep the pre-#24241 fetchMe-first path unchanged.
 *
 * Branches, tried in order:
 *
 *  0. Grant-first pre-step (gated on {@link AuthBootDeps.hasStoredToken}).
 *     Grant + bearer identity + cookie identity, with THREE valid outcomes:
 *     - cookie identity present AND matches the bearer id → `ledger_grant`
 *       (the same-site local-dev case, and any production topology where
 *       the app and API share a registrable domain);
 *     - cookie identity present AND does NOT match the bearer id → stale
 *       token for a different user; clear it and fall through;
 *     - `fetchIdentity` resolves `{user:null}` (the production
 *       `app.wagepro.ai` → `api.ledgerpro.ai` cross-site case where the
 *       Ledger `SameSite=Lax` cookie is never sent, #25227) → `ledger_grant`
 *       using the bearer identity `{ userId, email }`; ALSO turn on the
 *       admin tRPC client's bearer fallback flag. A `fetchIdentity`
 *       REJECTION is NOT conflated with `{user:null}` — that would let a
 *       transient auth.me failure on B's boot promote A's stale token
 *       into B's org, defeating #24241 — so a throw falls through
 *       without clearing.
 *  1. `wageproAuth.me` succeeds → `wagepro_company` (legacy WagePro-first
 *     standalone owner). Grant sourced from the returned `firm_id`/`entity_id`.
 *  2. Else fetch `auth.me`. Null user → `unauthenticated` (no session at all).
 *  3. Fetch `auth.oauthGrant`. If it returns both `firmId` and `entityId` →
 *     `ledger_member`/`employer` (the Ledger-first case #23952 exists to
 *     serve — cookie session AND a WagePro-audience OAuth token bound to the
 *     entity the user consented on). Skipped when step 0 already ran — the
 *     same token cannot yield a different grant on a second call.
 *  4. Else fall through to the ESS employee branch (own PersonalDO's
 *     `personalPayroll.listEmployerLinks`) → `employee_link` or
 *     `unauthenticated` if no active link exists.
 */
export async function resolveAuthBoot(
  deps: AuthBootDeps,
): Promise<AuthBootResult> {
  // Reset the admin bearer fallback to its pure-cookie default on every
  // boot. Only case (c) below will turn it on. This makes boot's own tRPC
  // calls (`fetchMe` / `fetchIdentity`, both cookie-transport) run
  // cookie-only regardless of what a previous boot left in module state,
  // so a same-site / standalone user's boot never accidentally attaches a
  // stale bearer to `wageproAuth.me` / `auth.me`.
  deps.setBearerFallback(false);

  // Step 0: grant-first pre-step (#24241). Only runs when a WagePro-audience
  // bearer token pair is stored — see the ordering rule in the docblock
  // above.
  const tokenStored = await deps.hasStoredToken();
  if (tokenStored) {
    const grantBoot = await tryLedgerGrantFirstBoot(deps);
    if (grantBoot) return grantBoot;
    // Fell through: grant returned null/incomplete, threw, cookie identity
    // was not resolvable, or a bearer/cookie identity mismatch cleared the
    // stale token. Continue with the standard 4-step order.
  }

  // Step 1: WagePro-first standalone owner (byte-identical to the pre-#23952
  // body). Success wins over every later branch.
  try {
    const me = await deps.fetchMe();
    return {
      status: 'authenticated',
      identity: { userId: me.user_id, email: me.email, fullName: me.full_name },
      grant: {
        firmId: me.firm_id,
        entityId: me.entity_id,
        mode: 'write',
        scopes: [...FULL_ACCESS_SCOPES],
      },
      sessionKind: 'employer',
      employee: null,
      bootSource: 'wagepro_company',
    };
  } catch {
    // Fall through to the identity + grant / employee-link resolution below.
  }

  // Step 2: is there a session at all? Reject early if not — every branch
  // below needs `auth.me` to have resolved a real user.
  let identity: { userId: string; email: string; fullName: string };
  try {
    const { user } = await deps.fetchIdentity();
    if (!user) return UNAUTHENTICATED_BOOT;
    identity = { userId: user.id, email: user.email, fullName: user.full_name };
  } catch {
    return UNAUTHENTICATED_BOOT;
  }

  // Step 3: OAuth grant path — only reachable when step 0 was skipped
  // (`!tokenStored`), so we never re-issue the same grant query the token-
  // gated step 0 already answered.
  if (!tokenStored) {
    let claim: OAuthGrantClaim | null = null;
    try {
      claim = await deps.fetchOAuthGrant();
    } catch {
      // Treat a failed grant lookup the same as "no grant" — fall through to
      // the employee branch. `auth.oauthGrant` throws only on protocol shape
      // errors (missing scope, etc.); a null-audience session-cookie caller
      // resolves the endpoint successfully with all-null fields, which we
      // handle just below.
    }
    if (claim && claim.firmId !== null && claim.entityId !== null) {
      return {
        status: 'authenticated',
        identity,
        grant: {
          firmId: claim.firmId,
          entityId: claim.entityId,
          mode: claim.mode ?? 'write',
          scopes:
            claim.scopes.length > 0
              ? [...claim.scopes]
              : [...FULL_ACCESS_SCOPES],
        },
        sessionKind: 'employer',
        employee: null,
        bootSource: 'ledger_grant',
      };
    }
  }

  // Step 4: ESS employee fallback — the caller has a real session but no
  // WagePro company row AND no Ledger OAuth grant. Only an active employer
  // link is left as a real session.
  return resolveEmployeeBoot(deps, identity);
}

/**
 * Grant-first pre-step for {@link resolveAuthBoot} — issues
 * {@link AuthBootDeps.fetchOAuthGrant} and, when it names both a firm and an
 * entity, decides between three outcomes based on the token's bearer
 * identity and the cookie session identity:
 *
 *  (a) cookie identity present AND matches the bearer id → boot the
 *      `ledger_grant` using the cookie identity (its `full_name` is richer
 *      than the bearer's minimal `{userId,email}` claim);
 *  (b) cookie identity present AND does NOT match the bearer id → stale
 *      token from a DIFFERENT user (returning `ledger_grant` there would
 *      boot user B into user A's org with B's name — no IDOR, but a
 *      wrong-org boot). Clear the stale token via
 *      {@link AuthBootDeps.clearBearerTokens} and return `null` so the
 *      caller falls through to the fetchMe-first path;
 *  (c) NO cookie identity — ONLY `fetchIdentity` resolved `{user:null}`
 *      counts here (the production `app.wagepro.ai` → `api.ledgerpro.ai`
 *      cross-site case where the Ledger `SameSite=Lax` cookie is never
 *      sent). Boot the `ledger_grant` using the bearer identity
 *      `{ userId, email, fullName: '' }` and turn on the admin tRPC
 *      client's bearer fallback via
 *      {@link AuthBootDeps.setBearerFallback}. Boot traffic itself runs
 *      cookie-only (the flag is off entering `resolveAuthBoot`), so
 *      `fetchIdentity` cannot resolve `{user:null}` because a request
 *      unexpectedly resolved as an OAuth caller — it means the cookie
 *      really is unreachable. `wagepro_store_identity.ts`'s
 *      `deriveSessionFromIdentity` falls back to the email local-part
 *      when `fullName` is blank, so the display name is still populated.
 *
 * A THROW from `fetchIdentity` (network, 5xx, TOO_MANY_REQUESTS) is NOT
 * conflated with `{user:null}` and must never take case (c) — the
 * cross-check exists precisely to stop A's stale token pairing with B's
 * cookie, and letting a transient auth.me failure on B's boot swap A's
 * token in would defeat #24241. Returns `null` for every unhappy outcome
 * (grant null/incomplete, grant threw, bearer identity threw or returned
 * `null`, cookie identity threw) WITHOUT clearing — those are transient
 * and must not wipe a real token.
 */
async function tryLedgerGrantFirstBoot(
  deps: AuthBootDeps,
): Promise<AuthBootResult | null> {
  let claim: OAuthGrantClaim | null;
  try {
    claim = await deps.fetchOAuthGrant();
  } catch {
    return null;
  }
  if (!claim || claim.firmId === null || claim.entityId === null) return null;

  let bearer: BearerIdentityResult;
  try {
    const result = await deps.fetchBearerIdentity();
    if (!result) return null;
    bearer = result;
  } catch {
    return null;
  }

  let cookieUser: { id: string; email: string; full_name: string } | null;
  try {
    const { user } = await deps.fetchIdentity();
    cookieUser = user;
  } catch {
    // A throw here is transient (network, 5xx, TOO_MANY_REQUESTS) — never
    // conflate it with `{user:null}`. Falling into case (c) on a throw
    // would let user B's boot, one flaky auth.me away, silently promote
    // user A's stale token into B's org (see #24241). Fall through
    // without clearing so a real cookie session on the next boot resolves
    // normally.
    return null;
  }

  if (cookieUser === null) {
    // Case (c): the production Ledger-SSO handoff to
    // `app.wagepro.ai` → `api.ledgerpro.ai`. Boot from the bearer identity
    // — the cross-check has nothing on the cookie side to run against,
    // and the cookie truly is unreachable (boot traffic is cookie-only
    // because the fallback flag is still off here). Turn the fallback on
    // BEFORE returning so every subsequent tRPC call attaches the bearer.
    deps.setBearerFallback(true);
    return buildLedgerGrantBoot(claim, {
      userId: bearer.userId,
      email: bearer.email,
      fullName: '',
    });
  }

  if (bearer.userId !== cookieUser.id) {
    // Case (b): stale token from a different user. Clear it and fall
    // through so the caller resolves this cookie session via fetchMe.
    try {
      await deps.clearBearerTokens();
    } catch (err) {
      console.error(
        '[wagepro-web] clearing stale bearer tokens after identity mismatch failed:',
        err,
      );
    }
    return null;
  }

  // Case (a): cookie and bearer agree — prefer the cookie identity's
  // richer `full_name` over the bearer's minimal `{userId,email}` claim.
  return buildLedgerGrantBoot(claim, {
    userId: cookieUser.id,
    email: cookieUser.email,
    fullName: cookieUser.full_name,
  });
}

function buildLedgerGrantBoot(
  claim: OAuthGrantClaim,
  identity: AuthIdentity,
): AuthBootResult {
  return {
    status: 'authenticated',
    identity,
    grant: {
      firmId: claim.firmId,
      entityId: claim.entityId,
      mode: claim.mode ?? 'write',
      scopes:
        claim.scopes.length > 0 ? [...claim.scopes] : [...FULL_ACCESS_SCOPES],
    },
    sessionKind: 'employer',
    employee: null,
    bootSource: 'ledger_grant',
  };
}

/**
 * Employee (self-service) fallback, reached only after `wageproAuth.me`
 * rejects AND no OAuth grant tied this session to a Ledger entity (see
 * {@link resolveAuthBoot} for the ordering).
 *
 * `identity` has already been resolved by the caller (step 2 above), so this
 * only issues `personalPayroll.listEmployerLinks` — no active link means the
 * caller is a Ledger-first admin whose WagePro consent has not (or no
 * longer) been granted, which resolves to `needs_ledger_sso`. Routing them
 * to the plain WagePro `/login` credentials form would 403 (`wageproAuth.
 * login` rejects any user with no `wagepro_companies` row and no employee
 * link); `AuthGate` sends them to `buildLedgerSsoLoginPath()` instead so
 * the "Sign in with Ledger" PKCE handoff kicks off immediately.
 *
 * A `listEmployerLinks` fetch failure is treated the same way — we already
 * proved a cookie identity exists (step 2), so falling back to plain
 * `unauthenticated` would send a Ledger-first admin whose employee-link
 * lookup failed for a transient reason onto the wrong form. The SSO
 * handoff is idempotent: if a valid grant is minted, the next boot lands
 * back on the `ledger_grant` branch.
 *
 * The resulting grant carries `firmId: null` DELIBERATELY. `resolveOAuthEntityContext`
 * (`oauth_entity_context.ts`) requires both ids to report `ready`, so every
 * employer-scoped hook in the app (`useSessionFacts`, `useEssInvites`,
 * `useEmployees`, …) sees `no_entity` and never issues a query an employee
 * would be forbidden from making. `entityId` is still populated because the
 * self-service reads (`listPayslips`) are addressed by entity.
 *
 * Only the FIRST active link is used. Multi-employer self-service is a real
 * product question (which employer does My Pay open on?) that this path does
 * not decide — `listEmployerLinks` is ordered by `linked_at`, so this is the
 * earliest link, and `MyPayScreen` does its own employer selection from the
 * same procedure once booted.
 */
async function resolveEmployeeBoot(
  deps: AuthBootDeps,
  identity: { userId: string; email: string; fullName: string },
): Promise<AuthBootResult> {
  try {
    const { links } = await deps.fetchEmployerLinks();
    const link = links.at(0);
    if (!link) return NEEDS_LEDGER_SSO_BOOT;

    return {
      status: 'authenticated',
      identity,
      grant: {
        firmId: null,
        entityId: link.entity_id,
        mode: 'read',
        scopes: [...SELF_ONLY_SCOPES],
      },
      sessionKind: 'employee',
      employee: {
        entityId: link.entity_id,
        employeeId: link.employee_id,
        entityName: link.entity_name,
      },
      bootSource: 'employee_link',
    };
  } catch {
    return NEEDS_LEDGER_SSO_BOOT;
  }
}

async function fetchMeViaTrpc(): Promise<WageproMeResult> {
  return getTrpcClient().wageproAuth.me.query();
}

async function fetchIdentityViaTrpc(): Promise<AuthIdentityResult> {
  const result = await getTrpcClient().auth.me.query();
  // `auth.me` returns a much wider user row (timezone/locale/avatar/…); narrow
  // it here so `resolveAuthBoot` stays testable against a 3-field shape.
  const user = result.user;
  return {
    user: user
      ? { id: user.id, email: user.email, full_name: user.full_name }
      : null,
  };
}

async function fetchEmployerLinksViaTrpc(): Promise<EmployerLinksResult> {
  const result =
    await getTrpcClient().personalPayroll.listEmployerLinks.query();
  return {
    links: result.links.map((link) => ({
      entity_id: link.entity_id,
      employee_id: link.employee_id,
      entity_name: link.entity_name,
    })),
  };
}

/**
 * Ask `auth.oauthGrant` (over the BEARER client, not the session client) what
 * `(firmId, entityId, mode, scopes)` the caller's OAuth token is bound to.
 * Returns `null` when there is no stored bearer token — the endpoint would
 * still resolve `{all-null}` for a session-cookie caller, but skipping it
 * saves a round trip on every non-OAuth boot AND keeps the "cookie-session
 * WagePro user with no consent" case cleanly outside the ledger_grant branch
 * of {@link resolveAuthBoot} (which would otherwise get all-null fields and
 * correctly not activate — but never issuing the query is one less thing to
 * reason about).
 *
 * The bearer client is required because `auth.oauthGrant` echoes
 * `ctx.capability`, which is only populated for a valid OAuth token — a
 * session-cookie call resolves the endpoint but returns null capability fields,
 * which is not what we want here.
 */
async function fetchOAuthGrantViaTrpc(): Promise<OAuthGrantClaim | null> {
  if ((await getStoredTokens()) === null) return null;
  try {
    const result = await getBearerTrpcClient().auth.oauthGrant.query();
    return {
      firmId: result.firmId,
      entityId: result.entityId,
      mode: result.mode,
      scopes: [...result.scopes],
    };
  } catch {
    return null;
  }
}

/**
 * Ask `auth.identity` (over the BEARER client) which user the stored token
 * authenticates as — the minimal `{ userId, email }` PII-minimised identity
 * endpoint (`identity` OAuth scope). {@link resolveAuthBoot}'s grant-first
 * branch uses both fields: the id to cross-check against the cookie
 * session when one is reachable, and the (id, email) pair to boot from
 * directly when no cookie identity is reachable at all (#25227's
 * cross-site case).
 */
async function fetchBearerIdentityViaTrpc(): Promise<BearerIdentityResult | null> {
  const result = await getBearerTrpcClient().auth.identity.query();
  return { userId: result.userId, email: result.email };
}

export function AuthProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const [status, setStatus] = React.useState<AuthStatus>('loading');
  const [identity, setIdentity] = React.useState<AuthIdentity | null>(null);
  const [grant, setGrant] = React.useState<OAuthGrant | null>(null);
  const [sessionKind, setSessionKind] = React.useState<SessionKind | null>(
    null,
  );
  const [employee, setEmployee] = React.useState<EmployeeSelfContext | null>(
    null,
  );
  const [bootSource, setBootSource] = React.useState<AuthBootSource | null>(
    null,
  );
  const [bootAttempt, setBootAttempt] = React.useState(0);

  // Bind the bell's on-device notices to whoever is signed in. A different
  // user than their owner wipes them — this is what protects a shared phone
  // when the previous session expired instead of being signed out.
  const signed_in_user_id = identity?.userId;
  React.useEffect(() => {
    if (signed_in_user_id !== undefined) {
      claim_device_notifications(signed_in_user_id);
      // My leave's in-memory cache is keyed by user id on every read, so
      // this is belt-and-braces: free a previous user's figures promptly.
      claim_my_leave_cache(signed_in_user_id);
    } else {
      // Nobody signed in (boot resolved signed-out, e.g. an expired
      // session): no one may read the previous user's cached leave.
      clear_my_leave_cache();
    }
  }, [signed_in_user_id]);

  React.useEffect(() => {
    let cancelled = false;

    async function boot(): Promise<void> {
      // Local-dev-only convenience (see dev_auth_bypass.ts): make sure a
      // REAL session cookie exists for a REAL dev account BEFORE
      // `resolveAuthBoot` runs below. This still works unchanged — it
      // establishes a `mukil_session` cookie directly, the SAME cookie
      // `wageproAuth.login`/`wageproAuth.signup` issue, so the dev account
      // simply needs its own `wagepro_companies` row (a local seed concern,
      // not a code path this migration changes).
      if (
        import.meta.env.DEV &&
        import.meta.env.VITE_DEV_AUTH_BYPASS === DEV_AUTH_BYPASS_OPT_IN_VALUE
      ) {
        await ensureDevSession(
          resolveDevAuthCredentials({
            VITE_DEV_AUTH_EMAIL: import.meta.env.VITE_DEV_AUTH_EMAIL,
            VITE_DEV_AUTH_PASSWORD: import.meta.env.VITE_DEV_AUTH_PASSWORD,
          }),
        );
      }

      const result = await resolveAuthBoot({
        fetchMe: fetchMeViaTrpc,
        fetchIdentity: fetchIdentityViaTrpc,
        fetchEmployerLinks: fetchEmployerLinksViaTrpc,
        fetchOAuthGrant: fetchOAuthGrantViaTrpc,
        fetchBearerIdentity: fetchBearerIdentityViaTrpc,
        hasStoredToken: async () => (await getStoredTokens()) !== null,
        clearBearerTokens: () => clearStoredTokens(),
        setBearerFallback: setAdminBearerFallback,
      });
      if (cancelled) return;
      setStatus(result.status);
      setIdentity(result.identity);
      setGrant(result.grant);
      setSessionKind(result.sessionKind);
      setEmployee(result.employee);
      setBootSource(result.bootSource);
    }

    void boot();
    return () => {
      cancelled = true;
    };
  }, [bootAttempt]);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      status,
      identity,
      grant,
      sessionKind,
      employee,
      bootSource,
      signOut: async () => {
        // Revokes the session server-side via the SAME generic `auth.logout`
        // every other first-party surface uses (`apps/api/src/trpc/routers/
        // auth.ts`) — it expires every cookie name the parser accepts,
        // which includes the standard `mukil_session`/`__Host-mukil_session`
        // this app's session was issued under (see `wagepro_auth.ts`'s
        // module docblock on why WagePro reuses that standard cookie name
        // rather than a WagePro-specific one), and — because a raw session
        // id presented as a Bearer header resolves to that same session —
        // it revokes the native shell's token too. For a bearer-backed
        // session (case (c) boot, #25227) that call is skipped: a
        // Ledger-SSO admin has no server-side cookie state `auth.logout`
        // could reach when the request goes out bearer-only (the API
        // would FORBIDDEN it — `auth.logout` is not in
        // `OAUTH_PROCEDURE_ALLOWLIST` for the WagePro audience), so we
        // save the guaranteed-failing call and its console.error, and
        // rely on {@link oauthSignOut} → `/oauth/revoke` (below) to
        // revoke the OAuth token instead. Then drops BOTH the locally
        // stored native token AND the WagePro-audience OAuth bearer
        // token pair, the latter via {@link oauthSignOut} so a
        // Ledger-first session is also revoked server-side via
        // `/oauth/revoke`. Whether or not either server call succeeds,
        // both local credentials are cleared and the admin tRPC client's
        // bearer fallback flag is turned off. See {@link performSignOut}
        // for the ordering rationale.
        const bearerBacked = isAdminBearerFallbackEnabled();
        await performSignOut({
          logout: bearerBacked
            ? () => Promise.resolve()
            : () => getTrpcClient().auth.logout.mutate(),
          clearNativeToken: () => clearNativeSessionToken(),
          clearBearerTokens: () => oauthSignOut(),
          setBearerFallback: setAdminBearerFallback,
          clearDeviceNotifications: () => {
            clear_device_notifications();
          },
          clearMyLeaveCache: () => {
            clear_my_leave_cache();
          },
        });
        setStatus('signed_out');
        setIdentity(null);
        setGrant(null);
        setSessionKind(null);
        setEmployee(null);
        setBootSource(null);
      },
      retryBoot: () => {
        setStatus('loading');
        setBootAttempt((attempt) => attempt + 1);
      },
    }),
    [status, identity, grant, sessionKind, employee, bootSource],
  );

  return (
    <AuthContext.Provider value={value}>
      <SignedInUserIdContext.Provider value={signed_in_user_id ?? null}>
        {children}
      </SignedInUserIdContext.Provider>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}
